/**
 * Styles CF definitions (DL-61877 epic) — one per widget type used by the inline-edit e2e
 * suite, plus a NestedAllowedValue mother/child pair and a brand/group/category-scoped
 * "Advanced" field. Shape mirrors the real `/api/meta/styles/custom-fields` response
 * consumed by `useStyleCustomFieldDefinitionsFromMeta` (`src/features/styles/api/`).
 *
 * `allowedValues[].id` is deliberately a STRING (`'7031'`) while `allowedValues[].parentId`
 * and every row-level allowed-value id (`customFields[].value.id` / `.values[].id` on
 * `stylesData`, e.g. `7032`, `7051`) are numeric — this is NOT a mock inconsistency to
 * "fix" by unifying the type. It is a verified, faithful mirror of the real backend
 * contract: `useStyleCustomFieldDefinitionsFromMeta.ts`'s own `MetaItem` type (annotated
 * "Mirrors backend StyleCustomFieldMetaItem") types `allowedValues[].id` as `string` and
 * `parentId` as `number | null` in that SAME array, and its `toCustomFieldDefinition`
 * mapper explicitly does `id: Number(av.id)` to bridge the two — proof the real API
 * genuinely returns a string id in the meta response while every row-level reference to
 * that same id is numeric. A reverse `allowedValues.find(v => v.id === someNumericId)`
 * lookup must convert one side first (`String(someNumericId)`), exactly as the real
 * transform code already does — never assume the two ids share a type.
 *
 * Split out of `styles.ts` (DL-62594) purely to keep that file under the repo's 800-line cap
 * (`.eslintrc.js` `max-lines`) — same pattern as `itemsFixtureData.ts` for `items.ts`. No
 * behavior change, same values, same shapes.
 */
export const styleCustomFieldDefinitions = [
  {
    id: 701,
    name: 'Fabric Notes',
    position: 1,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 255,
  },
  {
    id: 702,
    name: 'Delivery Date',
    position: 2,
    type: 1, // Date
  },
  {
    id: 703,
    name: 'Fit Type',
    position: 3,
    type: 0, // AllowedValue (single-select)
    isMultipleAllowedValue: false,
    allowedValues: [
      { id: '7031', value: '7031', displayName: 'Slim' },
      { id: '7032', value: '7032', displayName: 'Regular' },
      { id: '7033', value: '7033', displayName: 'Loose' },
    ],
  },
  {
    id: 704,
    name: 'Fabric Blend',
    position: 4,
    type: 0, // AllowedValue (multi-select)
    isMultipleAllowedValue: true,
    // 12 options (DL-62704/DL-62718, Testmo 2339495) — deliberately more than fit in the
    // dropdown's visible listbox height, so its "Set empty" FIXED footer genuinely has an
    // internally-scrolling option list to sit below (the Testmo case's own point: the footer
    // must stay visible and non-overlapping while the list scrolls).
    allowedValues: [
      { id: '7041', value: '7041', displayName: 'Cotton' },
      { id: '7042', value: '7042', displayName: 'Polyester' },
      { id: '7043', value: '7043', displayName: 'Wool' },
      { id: '7044', value: '7044', displayName: 'Elastane' },
      { id: '7045', value: '7045', displayName: 'Silk' },
      { id: '7046', value: '7046', displayName: 'Linen' },
      { id: '7047', value: '7047', displayName: 'Nylon' },
      { id: '7048', value: '7048', displayName: 'Viscose' },
      { id: '7049', value: '7049', displayName: 'Cashmere' },
      { id: '70410', value: '70410', displayName: 'Denim' },
      { id: '70411', value: '70411', displayName: 'Corduroy' },
      { id: '70412', value: '70412', displayName: 'Velvet' },
    ],
  },
  {
    id: 705,
    name: 'Print Type',
    position: 5,
    type: 3, // NestedAllowedValue — mother (no parentCustomFieldId)
    allowedValues: [
      { id: '7051', value: '7051', displayName: 'Screen Print', parentId: null },
      { id: '7052', value: '7052', displayName: 'Embroidery', parentId: null },
    ],
  },
  {
    id: 706,
    name: 'Print Placement',
    position: 6,
    type: 3, // NestedAllowedValue — child of 705
    parentCustomFieldId: 705,
    parentCustomFieldName: 'Print Type',
    allowedValues: [
      { id: '7061', value: '7061', displayName: 'Chest', parentId: 7051 },
      { id: '7062', value: '7062', displayName: 'Sleeve', parentId: 7051 },
      { id: '7063', value: '7063', displayName: 'Back', parentId: 7052 },
      { id: '7064', value: '7064', displayName: 'Collar', parentId: 7052 },
    ],
  },
  {
    id: 707,
    name: 'Sustainability Rating',
    position: 7,
    type: 0, // AllowedValue (single-select), scoped — DL-61911
    isMultipleAllowedValue: false,
    hasGroupScope: true,
    hasCategoryScope: true,
    allowedValues: [
      { id: '7071', value: '7071', displayName: 'Bronze' },
      { id: '7072', value: '7072', displayName: 'Silver' },
      { id: '7073', value: '7073', displayName: 'Gold' },
    ],
  },
  {
    // DL-62704/DL-62715, Testmo 2339497 — optional Numeric CF for the "Set empty" per-field-type
    // behavior spec; no existing Numeric-type definition was seeded here before.
    id: 708,
    name: 'Target Weight (g)',
    position: 8,
    type: 4, // Numeric
    allowedDecimals: 0,
  },
];
